import { identity, isEmpty, trim, merge, find, forEach } from 'lodash'
import { normalize, arrayOf, Schema } from 'normalizr'
import { camelize, singularize, tableize, underscore } from 'inflected'

import API from 'shared/lib/api'
import ORM from './orm'
import Model from './model'
import strategies from './strategies'

/**
 * Redux Model
 *
 * This module provides a way to create Redux models that integrate with the ORM system.
 *
 * @param {string} modelName - The name of the model (CamelCase)
 * @param {Object} options - Configuration options
 * @param {string} [options.singularName] - The singular name for the model (snake_case)
 * @param {string} [options.pluralName] - The plural name for the model (snake_case)
 * @param {string} [options.stateKey] - The key used to store this model's data in the Redux state.
 *                                      If not provided, defaults to the model's plural name.
 *                                      This is useful when you want to store the model's data
 *                                      under a different key than its plural name, or when
 *                                      multiple models need to share the same state slice.
 * @param {Object} [options.defaultValues] - Default values for model instances
 * @param {Object} [options.belongsTo] - Belongs-to associations
 * @param {Object} [options.hasMany] - Has-many associations
 * @param {Object} [options.hasOne] - Has-one associations
 * @param {string} [options.apiVersion] - API version to use (if different from default)
 * @param {string} [options.normalizeSingularKey] - Key to use when normalizing singular responses
 * @param {Function} [options.transformAttributes] - Function to transform attributes before storing them in the Redux state
 * @param {Object|Schema} [options.schema] - Custom normalizr schema
 */

const hasData = (data) => !isEmpty(trim(data))

export default (modelName, options = {}) => {
  if (options.apiVersion === API.defaultVersion) {
    throw 'Only specify apiVersion when different from the default.'
  }

  const singularName = options.singularName || underscore(modelName)
  const pluralName = options.pluralName || tableize(modelName)
  const standardStates = ['START', 'SUCCESS', 'ERROR']

  options.transformAttributes = options.transformAttributes || identity
  options.normalizeSingularKey ||= singularName

  class ReduxModel extends Model {
    // Returns the Redux store.
    //
    static store () {
      return window.store || window.booqableStore
    }

    // Returns actions wrapped in a dispatch function for use with bindActionCreators.
    //
    // @example
    //   bindActionCreators(SomeModel.actionsForBind(), dispatch)
    //
    static actionsForBind () {
      if (this.boundActions) return this.boundActions

      this.boundActions ||= {}

      forEach(this.actions, (func, name) => {
        this.boundActions[name] = (...args) => (dispatch) => func(...args, dispatch)
      })

      return this.boundActions
    }

    // Hydrates model with given data (if any). This dispatches an event.
    //
    static hydrate (data, options = {}) {
      const hasData = !isEmpty(trim(data))

      if (hasData) {
        const normalizedResponse = this._normalizeResponse({ data })
        const payload = {
          response: data,
          normalizedResponse,
          options,
          records: normalizedResponse.entities[pluralName]
        }

        return ReduxModel.store().dispatch({ type: this.actionTypes.FETCH_ALL_SUCCESS, payload })
      }
    }

    // Create a strategy containing the action and how the collection will be reduced.
    //
    // @example
    //   actionMethod = (model, options) ->
    //     API.updateResource model.pluralName, options.id, someModel: options.attributes
    //
    //   reducerMethod = (model, collection, action) ->
    //     switch action.type
    //       when model.actionTypes.UPDATE_START
    //         collection = collection.update(options.id, attributes)
    //
    //   SomeModel.createStrategy 'update', actionMethod, reducerMethod
    //
    static createStrategy (name, actionMethod, reducerMethod) {
      this.strategies ||= {}

      if (typeof actionMethod === 'object') {
        const _options = actionMethod
        actionMethod = _options.action
        reducerMethod = _options.reducer
      }

      const type = underscore(name).toUpperCase()

      if (actionMethod) {
        this.createAction(type, (...args) => actionMethod(this, ...args))
      }

      if (reducerMethod) {
        this.createReducer(reducerMethod)
      } else {
        this.createReducer((model, collection, action) => {
          const _options = action?.payload?.options || {}

          if (_options.id && collection.getById(_options.id)) {
            const flagName = (flag) => camelize(`${flag}_${name}`, false)

            switch (action.type) {
              case model.actionTypes[`${type}_START`]: {
                const data = {
                  isBusy: true,
                  [flagName('pending')]: true,
                  [flagName('succss')]: false
                }

                // Set pending flag
                collection.update(_options.id, data)
                break
              }
              case model.actionTypes[`${type}_ERROR`]: {
                const data = {
                  isBusy: false,
                  hasError: true,
                  errorData: action?.payload?.error,
                  [flagName('pending')]: false,
                  [flagName('success')]: false,
                  [flagName('error')]: true,
                  [flagName('errorData')]: action?.payload?.error
                }

                // Set error flag
                collection.update(_options.id, data)
                break
              }
              case model.actionTypes[`${type}_SUCCESS`]: {
                const data = {
                  isBusy: false,
                  hasError: false,
                  errorData: undefined,
                  [flagName('pending')]: false,
                  [flagName('success')]: true,
                  [flagName('error')]: false,
                  [flagName('errorData')]: undefined
                }

                // Remove pending flag
                collection.update(_options.id, data)
                break
              }
            }
          }
        })
      }
    }

    // Create a mini-reducer that will be used for reducing the collection.
    //
    // @example
    //   SomeModel.createReducer (method) ->
    //     // Do something with collection here
    //
    static createReducer (method) {
      this.reducers ||= []
      this.reducers.push(method)
    }

    // Creates an action, automatically handles START/SUCCESS/ERROR.
    //
    static createAction (type, func) {
      this.actions ||= {}

      this._createActionTypes(type)
      this._createActionCreators(type, 'records')

      const methodName = camelize(underscore(type), false)
      this.actions[methodName] = (options, dispatch) => {
        dispatch ||= ReduxModel.store().dispatch
        // Dispatch start
        dispatch(this.actionCreators[`${methodName}Start`]({ options }))

        return new Promise((resolve, reject) => {
          const onResolve = (response) => {
            let payload

            if (hasData(response.data)) {
              const normalizedResponse = this._normalizeResponse(response, options)
              payload = {
                response: response.data,
                normalizedResponse,
                options,
                records: normalizedResponse.entities[pluralName]
              }
            } else {
              payload = {
                response: response.data,
                options
              }
            }

            // Manually dispatch a delete action for associated models
            this._deleteNestedRelations(options?.attributes, dispatch)

            // Dispatch success
            resolve(dispatch(this.actionCreators[`${methodName}Success`](payload)))
          }

          const onReject = (error) => {
            let payload

            if (hasData(error.response.data)) {
              const normalizedResponse = this._normalizeResponse(error.response, options)
              payload = {
                error: error.response.data,
                normalizedResponse,
                options
              }
            } else {
              payload = {
                error: error.response,
                options
              }
            }

            // Dispatch error
            dispatch(this.actionCreators[`${methodName}Error`](payload))

            // Reject promise
            reject(error.response.data?.errors || error.response.data?.error)
          }

          return func(options).then(onResolve, onReject)
        })
      }
    }

    // Schema to normalize responses with.
    // If no schema is given it will create one from the associations.
    //
    static schema () {
      if (this._schema) return this._schema

      if (options.schema) {
        this._schema = options.schema
      } else {
        this._schema = new Schema(pluralName)

        forEach(options.belongsTo, (_options, _name) => {
          const name = underscore(_name)
          const model = ORM.models[name]
          if (model) this._schema.define({ [name]: model.schema() })
        })

        forEach(options.hasOne, (_options, _name) => {
          const name = underscore(_name)
          const model = ORM.models[name]
          if (model) this._schema.define({ [name]: model.schema() })
        })

        forEach(options.hasMany, (_options, _key) => {
          const _name = _options.modelName || _key
          const name = underscore(_name)
          const key = underscore(_key)

          const model = ORM.models[singularize(name)]
          if (model) this._schema.define({ [key]: arrayOf(model.schema()) })
        })
      }

      return this._schema
    }

    static clearIncludeChache (options = {}) {
      const includeTypes = options.include.split(',').map((s) => s.trim())
      const store = this.store()

      setTimeout(() => {
        includeTypes.forEach((includeType) => {
          const targetModel = Object.values(ORM.models).find((model) =>
            model.pluralName === includeType ||
            model.options?.stateKey === includeType
          )

          if (targetModel) {
            store.dispatch({
              type: targetModel._actionType('refresh', 'success'),
              payload: {
                clearCacheOnly: true,
                normalizedResponse: { entities: {} },
                options: {}
              }
            })
          }
        })
      }, 0) // Allow the state to update before fetching includes
    }

    // Reducer for used in the Redux state.
    //
    static getReducer () {
      return (collection, action) => {
        collection ||= this.collection()

        if (action.type === 'ORM_INVALIDATE_CACHE') {
          const skip = action.payload?.skip || []

          if (skip.indexOf(pluralName) === -1) {
            collection = collection.invalidateCache({ name: pluralName })
          }
        }

        // This logic is specific for the refresh strategy.
        // It clears the cache of the ORM before setting the new data.
        if (action.type === this._actionType('refresh', 'success')) {
          collection = collection.clearCache()
          const options = action.payload?.options || {}
          if (options.include) this.clearIncludeChache(options)
        }

        const data = action?.payload?.normalizedResponse?.entities[pluralName]
        if (data) {
          // Data is already normalized
          collection = collection.hydrate(data)
        } else {
          const response = action?.payload?.response
          if (response) {
            // If no normalized data is found for `pluralName` then check if we can
            // normalize the response in case collection data is given.
            // This is a pretty aggressive approach to collect any type of collection data.
            let hasPluralData, hasSingularData

            if (response?.data) {
              // Response is a JSON API response
              hasPluralData = true
            } else {
              hasSingularData = response?.[singularName] && !isEmpty(trim(response?.[singularName]))
              hasPluralData = response?.[pluralName] && !isEmpty(trim(response?.[pluralName]))
            }

            if (hasPluralData || hasSingularData) {
              const normalizedResponse = this._normalizeResponse({ data: response }, action?.payload?.options)
              const data = normalizedResponse?.entities[pluralName]

              if (data) {
                collection = collection.hydrate(data)
              }
            }
          }
        }

        // Call mini-reducers for each registered reducer
        forEach(this.reducers, (reducer) => {
          const _collection = reducer(this, collection, action)
          if (_collection) {
            collection = _collection
          }
        })

        return collection
      }
    }

    static _actionType (type, status = 'SUCCESS') {
      return [pluralName, type, status].join('_').toUpperCase()
    }

    static _actionTypes (...types) {
      const actions = {}

      types.forEach((type) => {
        standardStates.map((status) => this._actionType(type, status)).forEach((type) => {
          const name = type.replace(`${pluralName}_`.toLocaleUpperCase(), '')
          actions[name] = type
        })
      })

      return actions
    }

    static _createActionTypes (...types) {
      this.actionTypes ||= {}

      forEach(this._actionTypes(...types), (value, key) => {
        this.actionTypes[key] = value
      })

      return this.actionTypes
    }

    static _createActionCreator (name, type, _args) {
      this.actionCreators ||= {}

      const funcName = camelize(underscore(name), false)

      this.actionCreators[funcName] = (payload = {}) => ({ type, payload })
    }

    static _normalizeResponse (response, options = {}) {
      const json = response?.data || {}

      if (json.data) {
        return this._normalizeJsonApiResponse(json, options)
      } else {
        return this._normalizeLegacyResponse(json)
      }
    }

    static _normalizeJsonApiResponse (json, options = {}) {
      const data = {}

      const findModelByKey = (key) => {
        key = key.toLowerCase()

        if (ORM.__modelCache[key]) return ORM.__modelCache[key]

        const result = find(ORM.models, (_model, _name) => {
          return [_model.pluralName, _model.singularName, _model.options.normalizeSingularKey].indexOf(key) >= 0
        })

        ORM.__modelCache[key] = result

        return result
      }

      const addRecord = (data, record, type, stateKey) => {
        const model = findModelByKey(type || record.type)

        if (!model) return

        type = stateKey || model.options.stateKey || model.pluralName

        data[type] ||= {}
        data[type][record.id] = merge({ id: record.id }, model.options.transformAttributes(record.attributes))

        // Set ids of the relations just like normalizr
        forEach(record.relationships, (relationData, relation) => {
          let relatedIds

          if (Array.isArray(relationData.data)) {
            relatedIds = relationData.data.map((data) => data.id)
          } else if (relationData.data) {
            relatedIds = relationData.data.id
          }

          if (relatedIds) {
            data[type][record.id][relation] = relatedIds
            data[type][record.id][`${relation}_ids`] = relatedIds
          }
        })
      }

      if (Array.isArray(json.data)) {
        // Data for the collection itself
        forEach(json.data, (record) => {
          addRecord(data, record, null, options?.stateKey)
        })
      } else {
        // Data for a single record
        addRecord(data, json.data, null, options?.stateKey)

        if (json.data?.attributes?.type?.includes('::')) {
          addRecord(data, json.data, json.data.attributes.type.split('::')[0])
        }
      }

      if (json.included) {
        // Data for the associations
        forEach(json.included, (record) => {
          addRecord(data, record)
        })

        const includeNamePerId = {}

        forEach([json.data].flat(), (jsonData) => {
          // Find original include name so we can use that as the state key
          forEach(jsonData.relationships, (relationData, relation) => {
            let relatedIds

            relation = tableize(relation)

            if (Array.isArray(relationData.data)) {
              relatedIds = relationData.data.map((data) => data.id)
            } else if (relationData.data) {
              relatedIds = [relationData.data.id]
            }

            forEach(relatedIds, (id) => {
              includeNamePerId[id] = relation
            })
          })
        })

        // Data for the associations
        forEach(json.included, (record) => {
          const stateKey = includeNamePerId[record.id]
          addRecord(data, record, null, stateKey)
        })
      }

      return { entities: data }
    }

    static _normalizeLegacyResponse (json) {
      if (typeof json !== 'object' || json === null) return {}

      const schema = {}

      forEach(ORM.models, (_model, _name) => {
        if (_model && (json[_model.pluralName] || json[_model.options.normalizeSingularKey])) {
          // Response contains data for model, include it in the normalization
          schema[_model.pluralName] = arrayOf(_model.schema())
          schema[_model.options.normalizeSingularKey] = _model.schema()
        }
      })

      return normalize(json, schema)
    }

    static _createActionCreators (type) {
      this._createActionCreator(`${type}_START`, this.actionTypes[`${type}_START`])
      this._createActionCreator(`${type}_SUCCESS`, this.actionTypes[`${type}_SUCCESS`])
      this._createActionCreator(`${type}_ERROR`, this.actionTypes[`${type}_ERROR`])
    }

    static _deleteNestedRelations (attributes, dispatch) {
      if (!options.hasMany) return

      const relations = []

      forEach(options.hasMany, (_, attribute) => {
        relations.push(attribute)
      })

      const toBeDeleted = []

      if (attributes) {
        forEach(relations, (relation) => {
          if (attributes[`${relation}_attributes`]) {
            forEach(attributes[`${relation}_attributes`], (attribute) => {
              if (attribute._destroy) {
                toBeDeleted.push({ model: relation.toUpperCase(), id: attribute.id })
              }
            })
          }
        })
      }

      forEach(toBeDeleted, (item) => {
        dispatch({
          type: `${item.model}_DELETE_START`,
          payload: {
            options: {
              id: item.id,
              optimistic: true
            }
          }
        })
      })
    }
  }

  forEach(strategies, (obj, name) => {
    ReduxModel.createStrategy(name, obj.actionMethod, obj.reduceMethod)
  })

  forEach(options.belongsTo, (_options, _name) => {
    ReduxModel.belongsTo(_name, _options)
  })

  forEach(options.hasMany, (_options, _name) => {
    ReduxModel.hasMany(_name, _options)
  })

  forEach(options.hasOne, (_options, _name) => {
    ReduxModel.hasOne(_name, _options)
  })

  ReduxModel.singularName = singularName
  ReduxModel.pluralName = pluralName
  ReduxModel.options = options

  ReduxModel._asyncDefaultValues = {
    isBusy: false,
    hasError: false,
    errorData: undefined,
    pendingUpdate: false,
    pendingCreate: false,
    pendingDelete: false
  }

  ReduxModel.defaultValues(ReduxModel._asyncDefaultValues)

  if (options.defaultValues) {
    ReduxModel.defaultValues(options.defaultValues)
  }

  ORM.models[singularName] = ReduxModel

  return ReduxModel
}
